import type { FieldKind } from '@/api/docfiller-types'
import { isCheckboxChecked } from '@/api/docfiller-types'

/**
 * Humanise a stored field value for diff rendering. Mirrors the chat
 * normaliser so a checkbox proposal shows "Checked" instead of the wire
 * "true" / "false" strings, and empty strings render as a soft "Empty"
 * placeholder rather than a misleading blank cell.
 */
export function humanizeFieldValue(
  value: string | null | undefined,
  kind: FieldKind | null | undefined,
): { text: string; isEmpty: boolean } {
  if (value == null) return { text: 'Empty', isEmpty: true }
  const trimmed = value.trim()
  if (kind === 'checkbox') {
    const checked = isCheckboxChecked(trimmed || null)
    return { text: checked ? 'Checked' : 'Unchecked', isEmpty: false }
  }
  if (kind === 'signature') {
    return trimmed.length === 0
      ? { text: 'Unsigned', isEmpty: true }
      : { text: `Signed as ${trimmed}`, isEmpty: false }
  }
  if (trimmed.length === 0) {
    return { text: 'Empty', isEmpty: true }
  }
  return { text: trimmed, isEmpty: false }
}

/**
 * `true` when before and after carry the same logical value for a given
 * field kind. Used by the field-edit card to suppress redundant action
 * rows ("change Checked to Checked") in batch suggestions.
 */
export function isNoOpFieldChange(
  before: string | null | undefined,
  after: string,
  kind: FieldKind | null | undefined,
): boolean {
  const b = humanizeFieldValue(before, kind).text
  const a = humanizeFieldValue(after, kind).text
  return b === a
}

export function formatConfidencePct(confidence: number | null | undefined): string | null {
  if (typeof confidence !== 'number' || !Number.isFinite(confidence)) return null
  return `${Math.round(confidence * 100)}%`
}
